<template>
  <div class="multisteps-form__panel border-radius-xl bg-white" data-animation="FadeIn">
    <h5 class="font-weight-bolder">{{ $t('eartag.Find_and_select_animal_to_be_retagged') }}</h5>
    <!--p class="mb-0 text-sm">{{ $t('eartag.Find_and_select_animal_to_be_retagged') }}</p-->
    <div class="multisteps-form__content">
      <div class="row mt-3">
        <div class="col-12">
          <div class="mb-4">
            <p class="fw-bold text-sm">{{ $t('eartag.Selected_animal') }} ({{ selectedAnimals.length }})</p>
            <div class="row">
              <div v-for="animal in selectedAnimals" :key="animal.id" class="col-md-6 mb-1">
                <div class="card border  text-sm border-success bg-light" @click="toggleSelect(animal)">                  
                  <span v-html="displayText(animal)"></span>
                  <span 
                    class="text-danger position-absolute top-1 end-0"
                    style="cursor: pointer; font-weight: bold; font-size: 25px; margin-right: 10px;"
                    @click.stop="toggleSelect(animal)"
                  >
                    ×
                  </span>
                </div>
              </div>
              <!--p class="text-danger text-sm" v-if="selectedAnimals.length === 0" style="margin-bottom: 2px !important;">
                {{ $t('common.No_animal_selected') }}
              </p-->
              <p class="text-danger text-sm" v-if="v$.selectedAnimalId.$error">
                {{ v$.selectedAnimalId.$errors[0].$message }}
              </p>
            </div>
          </div>
        </div>
        <div class="col-6">
          <!-- Search Input -->
          <div class="input-group input-group-static null">
            <label class="">{{ $t('eartag.Search') }}</label>
            <input v-model="searchQuery" type="text" class="form-control form-control-default" :placeholder="$t('eartag.Search_UBN_studbooknumber_animalnumber_name')"> 
          </div>
        </div>
        <div class="col-12 mt-3">
          <!-- Available animals -->
          <p class="fw-bold text-sm">{{ $t('eartag.Available_animals') }}</p>
          <div class="row">
            <div v-for="animal in filteredAnimals" :key="animal.id" class="col-md-6 mb-1">
              <div class="card border text-sm" :class="{ 'border-success bg-light': isSelected(animal.id), 'cursor-pointer': true }" @click="toggleSelect(animal)">                
                <span v-html="displayText(animal)"></span>
              </div>
            </div>
          </div>
        </div>
      </div>      
      <div class="button-row d-flex mt-4">
        <button class="btn mb-0 bg-gradient-light btn-sm null null js-btn-prev" type="button" @click="validateAndPrevStep">{{ $t('common.Prev') }}</button>
        <button class="btn mb-0 btn-sm bg-gradient-success null null ms-auto js-btn-next" type="button" @click="validateAndNextStep">{{ $t('common.Next') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
  import { computed, reactive, ref, watch } from 'vue'
  import { required, helpers } from '@vuelidate/validators'
  import useVuelidate from '@vuelidate/core'
  import { useI18n } from 'vue-i18n'

  export default {
    name: "ReTaggingAnimal",
    props: {
      animals: { type: Array, default: () => [] },
      formData: { type: Object, required: true },
      prevStep: Function,
      nextStep: Function
    },
    setup(props, { emit }) {
      const { t } = useI18n()
      const localFormData = reactive(props.formData)
      
      const searchQuery = ref('')
      const selectedAnimalId = ref(
        Array.isArray(localFormData.animalId)
          ? localFormData.animalId
          : (localFormData.animalId ? [localFormData.animalId] : [])
      )
      const isSelected = (id) => selectedAnimalId.value.includes(id)

      const toggleSelect = (animal) => {
        const isAlreadySelected = selectedAnimalId.value.includes(animal.id)

        // If already selected, unselect it
        if (isAlreadySelected) {
          selectedAnimalId.value = []
        } else {
          selectedAnimalId.value = [animal.id]
        }
      }

      const displayText = (f) => {
        let parts = [];

        if (f.studbookNumber) parts.push(f.studbookNumber);
        if (f.animalNumber) parts.push(f.animalNumber);
        if (f.name) parts.push(`(${f.name})`);
        if (f.arrivalDate) {
            const dateObj = new Date(f.arrivalDate);
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            const formattedDate = `${day}-${month}-${year}`;

            parts.push(`<span style="color: green">${formattedDate}</span>`);
        }
        if (f.breedCode) parts.push(`(${f.breedCode})`);
        return parts.join(' | ');
      }
      
      const selectedAnimals = computed(() =>
        (props.animals || []).filter(f => selectedAnimalId.value.includes(f.id))
      )

      const filteredAnimals = computed(() =>
        (props.animals || []).filter(f =>
          !selectedAnimalId.value.includes(f.id) &&
          displayText(f).toLowerCase().includes((searchQuery.value || '').toLowerCase())
        )
      )

      const hydrateFromSelection = () => {
        const selId = selectedAnimalId.value[0]
        if (!selId) return
        const selected = (props.animals || []).find(a => a.id === selId)
        if (!selected) return

        localFormData.animalId = selId
        localFormData.animalDetails = displayText(selected)
      }

      watch(localFormData, () => emit('update:formData', localFormData), { deep: true })

      watch(selectedAnimalId, hydrateFromSelection, { immediate: true })
      watch(() => props.animals, hydrateFromSelection, { immediate: true, deep: true })

      // Vuelidate rules – require at least one selected animal
      const rules = computed(() => ({
        selectedAnimalId: {
          required: helpers.withMessage(t('eartag.No_animal_selected'), required)
        }
      }))
            
      const v$ = useVuelidate(rules, { selectedAnimalId })

      watch(localFormData, () => {
        emit('update:formData', localFormData)
      }, { deep: true })      

      const validateAndPrevStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {    
          hydrateFromSelection()
          props.prevStep && props.prevStep()
        }
      }

      const validateAndNextStep = async () => {
        v$.value.$touch()
        if (v$.value.$pending) {
          await v$.value.$validate()
        }
        if (!v$.value.$invalid) {
          hydrateFromSelection()
          props.nextStep && props.nextStep()
        }
      }

      return {
        localFormData,
        searchQuery,
        selectedAnimalId,
        selectedAnimals,
        filteredAnimals,
        isSelected,
        toggleSelect,
        displayText,
        v$,
        validateAndPrevStep,
        validateAndNextStep
      }
    }    
};
</script>
<style scoped>
  .card {
    transition: background-color 0.2s ease-in-out;
    cursor: pointer;
    padding: 10px !important;
  }
</style>